// ma-disenrollment-analysis.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Data & Research',
  stats: [
    { number: '15.6%', label: 'Leave in Year 1', color: 'red' },
    { number: '49%', label: 'Leave by Year 5', color: 'amber' },
    { number: 'Cost', label: '#1 Reason to Leave', color: 'blue' },
    { number: 'Quality', label: '#2 Reason to Leave', color: 'purple' }
  ],

  html: `
<p>If you want to prevent disenrollment, you need to understand why it happens. CMS tracks disenrollment reasons through surveys and data analysis. This research reveals patterns that can inform your retention strategy.</p>

<h2>The Scale of Disenrollment</h2>

<p>Medicare Advantage disenrollment is substantial:</p>

<ul>
<li><strong>First-year churn:</strong> 15.6% of new enrollees leave within 12 months</li>
<li><strong>Cumulative 5-year:</strong> 49% have changed insurance by year 5</li>
<li><strong>Annual rate:</strong> Approximately 1 in 10 switch plans each year</li>
<li><strong>Dual vs. non-dual:</strong> 53% of duals leave within 5 years vs. 48% of non-duals</li>
</ul>

<p>These numbers represent millions of beneficiaries making coverage changes—and millions of opportunities for retention or loss.</p>

<h2>CMS Disenrollment Reasons Survey</h2>

<p>CMS conducts the MA and PDP Disenrollment Reasons Survey to understand why people leave their plans. Key findings:</p>

<h3>Primary Disenrollment Reasons</h3>

<p><strong>1. Cost Factors (Most Cited)</strong></p>
<ul>
<li>Premium increases</li>
<li>Out-of-pocket cost surprises</li>
<li>Drug costs higher than expected</li>
<li>Overall value perception declined</li>
</ul>

<p><strong>2. Quality and Access Issues</strong></p>
<ul>
<li>Provider network problems (doctor left network)</li>
<li>Pharmacy network limitations</li>
<li>Prior authorization delays and denials</li>
<li>Quality of care concerns</li>
<li>Customer service dissatisfaction</li>
</ul>

<p><strong>3. Benefits and Coverage</strong></p>
<ul>
<li>Benefit reductions year-over-year</li>
<li>Formulary changes affecting medications</li>
<li>Better benefits perceived elsewhere</li>
<li>Coverage gaps discovered</li>
</ul>

<p><strong>4. Personal/Life Changes</strong></p>
<ul>
<li>Moved to new area (service area issue)</li>
<li>Lost Medicaid eligibility (D-SNP)</li>
<li>Health status change requiring different coverage</li>
<li>Spouse/family coverage coordination</li>
</ul>

<h2>Disenrollment Patterns by Time</h2>

<h3>When Do People Disenroll?</h3>

<p><strong>AEP (October 15 - December 7):</strong> Majority of voluntary switches. Full flexibility to change plans.</p>

<p><strong>OEP (January 1 - March 31):</strong> Secondary switching window. MA enrollees can make one change. First-year clients especially active.</p>

<p><strong>SEP (Year-round):</strong> Dual eligibles can switch quarterly. Others need qualifying events.</p>

<h3>First Year vs. Later Years</h3>

<p>First-year disenrollment is dramatically higher than subsequent years:</p>
<ul>
<li>Year 1: ~15.6% churn</li>
<li>Year 2-5: ~8-10% annual churn</li>
<li>Year 5+: Tends to stabilize lower</li>
</ul>

<p>The "loyalty cliff" in year 1 reflects clients discovering whether their plan meets expectations.</p>

<h2>Plan-Level Variation</h2>

<p>Disenrollment varies significantly by plan characteristics:</p>

<h3>Star Ratings Impact</h3>
<ul>
<li>Plans below 3 stars: Members have year-round SEP, higher disenrollment</li>
<li>Plans dropping from 4+ to below 4: Often see increased exits</li>
<li>5-star plans: Lowest disenrollment rates</li>
</ul>

<h3>Plan Stability</h3>
<ul>
<li>Plans with frequent network churn: Higher disenrollment</li>
<li>Plans with volatile benefits: Higher disenrollment</li>
<li>Consistent, stable plans: Lower disenrollment</li>
</ul>

<h3>Plan Type</h3>
<ul>
<li>HMO plans: Network restrictions can drive exits</li>
<li>PPO plans: Out-of-network options reduce network-driven exits</li>
<li>D-SNP plans: Higher baseline churn due to SEP flexibility</li>
</ul>

<h2>Demographic Patterns</h2>

<h3>Dual Eligibility</h3>
<p>Dual eligibles have 5% higher 5-year disenrollment than non-duals (53% vs. 48%). Contributing factors:</p>
<ul>
<li>Quarterly SEP eligibility</li>
<li>More complex coverage needs</li>
<li>Life situation volatility</li>
<li>Heavy D-SNP marketing</li>
</ul>

<h3>Health Status</h3>
<p>Higher medical utilization correlates with higher disenrollment:</p>
<ul>
<li>More interactions = more friction opportunities</li>
<li>More sensitive to network/formulary changes</li>
<li>Higher stakes in coverage quality</li>
</ul>

<h2>Applying Research to Practice</h2>

<h3>Address Cost Concerns Proactively</h3>
<p>Since cost is the #1 reason, before increases hit:</p>
<ul>
<li>Set rate increase expectations at enrollment</li>
<li>Proactive communication when increases announced</li>
<li>Total cost context (premium + OOP) vs. alternatives</li>
</ul>

<h3>Monitor Quality Indicators</h3>
<p>Track the factors that drive quality-related exits:</p>
<ul>
<li>Network stability for your clients' providers</li>
<li>Customer service reputation changes</li>
<li>Star rating trends</li>
<li>Prior auth denial patterns</li>
</ul>

<h3>First-Year Focus</h3>
<p>Given dramatically higher first-year churn:</p>
<ul>
<li>Enhanced onboarding for new enrollees</li>
<li>More frequent touchpoints in year 1</li>
<li>OEP monitoring for January-enrolled clients</li>
<li>Catch problems before they become exits</li>
</ul>

<h3>Population-Specific Strategies</h3>
<p>Different populations need different approaches:</p>
<ul>
<li>Dual eligibles: More intensive retention given quarterly SEP</li>
<li>High utilizers: Extra attention to coverage fit</li>
<li>Low-Star plan members: Proactive plan comparison</li>
</ul>

<h2>Tracking Your Own Disenrollment</h2>

<p>National research provides benchmarks. Track your specific disenrollment:</p>

<ul>
<li><strong>Exit interviews:</strong> Why did clients who left actually leave?</li>
<li><strong>Timing:</strong> When did they leave (AEP, OEP, SEP)?</li>
<li><strong>Patterns:</strong> Are certain plans, agents, or segments losing more?</li>
<li><strong>Preventability:</strong> Could you have intervened?</li>
</ul>

<p>Your own data is more actionable than national averages.</p>
  `,

  faqs: [
    {
      question: "What's the #1 reason people leave their Medicare Advantage plans?",
      answer: "Cost factors—premium increases, out-of-pocket surprises, and drug costs higher than expected. Quality and access issues (provider network problems, prior auth delays) are #2. Address cost concerns proactively by setting expectations and communicating increases early."
    },
    {
      question: "Why is first-year disenrollment so much higher than later years?",
      answer: "First-year clients are discovering whether their plan meets expectations. They haven't built loyalty yet. They're comparing expectations from enrollment to reality of using coverage. Research shows 15.6% leave in year 1 vs. 8-10% annually in subsequent years."
    },
    {
      question: "How does dual eligibility affect disenrollment?",
      answer: "Dual eligibles have 5% higher 5-year disenrollment (53% vs. 48%). They can switch plans quarterly via SEP (not just AEP), have more complex coverage needs, and are heavily marketed to by D-SNP plans year-round."
    },
    {
      question: "Do Star ratings affect disenrollment?",
      answer: "Yes significantly. Plans below 3 stars give members year-round SEP eligibility, making them constant churn risks. Plans that drop from 4+ stars often see increased exits as bonus-funded benefits decline. 5-star plans have lowest disenrollment."
    },
    {
      question: "How can I use disenrollment research in my practice?",
      answer: "Address cost concerns proactively, monitor quality indicators (network stability, Star ratings), focus extra attention on first-year clients, and apply population-specific strategies. Track your own disenrollment patterns—your data is more actionable than national averages."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage and Prescription Drug Plan Disenrollment Reasons Survey",
      url: "https://www.cms.gov/research-statistics-data-and-systems/research/cahps/mapdp_disenrollmentsurvey",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    },
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Understanding why clients leave is the first step to preventing it. Pro IRP helps you track disenrollment patterns in your own book, identify at-risk clients before they leave, and apply research-based retention strategies.`
};

export default content;
